import _ from 'lodash';
import moment from 'moment/moment';
import database from 'src/providers/watermelondb';
import {Q} from '@nozbe/watermelondb';

export default ({sessionSets, eventsBySetId, programTargets, program}: any) => {
  const analysisState = [];
  const analysis: any = {};

  for (const sessionId of Object.keys(sessionSets)) {
    const sessionSet = sessionSets[sessionId];

    const average =
      sessionSet.reduce((a: number, b: any) => {
        const setEvents = eventsBySetId[b.id] || [];
        return (
          a +
          setEvents.reduce((setPercentage: number, event: any) => {
            return (
              setPercentage +
              (event.value === 'success' ? 1 : 0) *
                (100 / programTargets?.length)
            );
          }, 0)
        );
      }, 0) / sessionSet.length;

    let state;

    if (analysisState[analysisState.length - 1]?.state === 'in-treatment') {
      if (average >= program?.masteryValue) {
        let staffMinimum = false;
        let consecutiveSessions = false;
        for (let i = analysisState.length - 1; i >= 0; i--) {
          if (analysisState[i].average < program?.masteryValue) {
            break;
          }

          if (analysisState.length - i > program?.masterySessions) {
            consecutiveSessions = true;
            break;
          }

          // TODO: check if it's greater than the number of staff minimum
          staffMinimum = true;
        }
        if (staffMinimum && consecutiveSessions) {
          if (program?.maintenance) {
            if (average >= program?.maintenanceValue) {
              state = 'in-maintenance';
            } else {
              state = 'in-treatment';
            }
          } else {
            state = 'completed';
          }
        } else {
          state = 'in-treatment';
        }
      } else {
        state = 'in-treatment';
      }
    } else if (
      analysisState[analysisState.length - 1]?.state === 'in-maintenance'
    ) {
      if (average >= program?.maintenanceValue) {
        state = 'in-maintenance';
      } else {
        state = 'in-treatment';
      }
    } else if (analysisState[analysisState.length - 1]?.state === 'completed') {
      state = 'completed';
    } else {
      state = 'in-treatment';
    }

    const sessionObject = {
      sessionId,
      average: average,
      state,
    };

    analysisState.push(sessionObject);
    analysis[sessionId] = sessionObject;
  }

  return {
    analysis,
    analysisState,
  };
};
// 1) Add this helper near the top:
const normalizePromptsArray = (ps: any): any[] => {
  if (Array.isArray(ps)) {
    return ps;
  }
  if (ps == null) {
    return [];
  }
  if (typeof ps === 'string') {
    try {
      const parsed = JSON.parse(ps);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // last resort: comma-separated string like "id1,id2"
      if (ps.includes(',')) {
        return ps
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean);
      }
      // single id as a string
      return [ps];
    }
  }
  // unknown shape
  return [];
};
/**
 * Robust Task Analysis CSV export that works for both:
 * - useObservables=true: Watermelon objects with nested relations (event.target.id, event.set.id)
 * - useObservables=false: HTTP-fetched flat objects (event.target_id, event.set_id)
 */
export const exportTaskAnalysis = async ({
  sets = [],
  targets = [],
  events = [],
  taskEvents = [],
  useObservables = false,
  startDate = undefined,
  endDate = undefined,
  session = undefined,
}: {
  sets: any[];
  targets: any[];
  events?: any[];
  taskEvents?: any[];
  useObservables?: boolean;
  startDate?: string | number | Date;
  endDate?: string | number | Date;
  session?: any;
}) => {
  const sortedTargets = [...(targets || [])];

  const val = (v: any, alt: any) => (v !== undefined && v !== null ? v : alt);
  const setId = (s: any) => val(s?.id, s?.set_id || s?.setId);
  const targetId = (t: any) => val(t?.id, t?.target_id || t?.targetId);

  const setStart = (s: any) =>
    val(s?.startTimestamp, s?.start_timestamp ?? s?.start);

  // Filter sets by date range
  const inRangeSets =
    startDate && endDate
      ? sets.filter((s: any) => {
          const ts = Number(setStart(s)) || 0;
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return ts >= start.getTime() && ts <= end.getTime();
        })
      : sets;

  const setIds = inRangeSets.map(s => setId(s));

  // Choose event source
  const filteredEvents = useObservables
    ? events
    : (taskEvents || []).filter((e: any) =>
        setIds.includes(val(e?.set_id, e?.set?.id)),
      );

  const eventsBySetId = !useObservables
    ? _.groupBy(filteredEvents, 'set_id')
    : _.groupBy(filteredEvents, (e: any) => e?.set?.id);

  // Collect prompt IDs for acronym lookup
  const collectPromptIds = (): string[] => {
    const ids = new globalThis.Set<string>();
    for (const e of filteredEvents) {
      const ps = normalizePromptsArray(e?.prompts);
      for (const p of ps) {
        if (typeof p === 'string') {
          ids.add(p);
        } else if (p?.id) {
          ids.add(p.id);
        }
      }
    }
    return Array.from(ids);
  };

  const promptAcronymsById: Record<string, string> = {};
  const promptIds = collectPromptIds();

  if (promptIds.length > 0) {
    const chunks = _.chunk(promptIds, 500);
    for (const ch of chunks) {
      const rows = await database
        .get('prompts')
        .query(Q.where('id', Q.oneOf(ch)))
        .fetch();
      for (const row of rows) {
        promptAcronymsById[row.id] = row?.acronym || row?.name || '';
      }
    }
  }

  const getPromptAcronyms = (ev: any): string[] => {
    const ps = normalizePromptsArray(ev?.prompts);
    if (ps.length === 0) {
      return [];
    }
    const acrs: string[] = [];
    for (const p of ps) {
      if (typeof p === 'string') {
        const acr = promptAcronymsById[p];
        if (acr) {
          acrs.push(acr);
        }
      } else if (p?.acronym) {
        acrs.push(p.acronym);
      } else if (p?.name) {
        acrs.push(p.name);
      } else if (p?.id && promptAcronymsById[p.id]) {
        acrs.push(promptAcronymsById[p.id]);
      }
    }
    return acrs.filter(Boolean);
  };

  const header = [
    'Date',
    'Set',
    ...sortedTargets.map((t: any, i: number) => `${t?.name}-${i + 1}`),
    'Score',
  ];

  const rows: any[] = [header];

  for (let i = 0; i < inRangeSets.length; i++) {
    const set = inRangeSets[i];
    const idx = i;
    const sid = setId(set);
    const setEvents: any[] = eventsBySetId[sid] || [];

    // Use session.sessionDate if available, else fallback to set.session_date
    const displayDate = session
      ? moment(session?.sessionDate).format('ll')
      : moment(set?.session_date).format('ll');

    const col: any[] = [displayDate, `Set ${idx + 1}`];

    for (const target of sortedTargets) {
      const tid = targetId(target);
      const ev =
        setEvents.find((e: any) => {
          const eTid = useObservables ? e?.target?.id : e?.target_id;
          return eTid === tid;
        }) || null;

      let icon = '-';
      if (ev) {
        if (ev?.value === 'success') {
          icon = 'O';
        } else if (ev?.value === 'error') {
          const acrs = getPromptAcronyms(ev);
          icon = acrs.length ? ['X', ...acrs].join('\n') : 'X';
        }
      }
      col.push(icon);
    }

    const successCount = (setEvents || []).filter(
      (e: any) => e?.value === 'success',
    ).length;
    const denom = Math.max(setEvents?.length || 0, 1);
    const score = Math.ceil((successCount / denom) * 100) + '%';
    col.push(score);

    rows.push(col);
  }

  return rows;
};
